import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

export default function useFacilityShifts({
  tenantId = "",
  facilityId = "",
  allowedFacilityIds = [],
  enabled = true,
  debugLabel = "FACILITY SHIFTS",
}) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const allowedFacilityKey = Array.isArray(
    allowedFacilityIds
  )
    ? allowedFacilityIds.join("|")
    : "";

  useEffect(() => {
    if (!enabled) {
      setShifts([]);
      setLoading(false);
      setError(null);

      return undefined;
    }

    if (!tenantId) {
      setShifts([]);
      setLoading(false);

      setError(
        new Error(
          "Facility shift listener requires tenantId."
        )
      );

      console.warn(
        `${debugLabel} NOT STARTED — tenantId missing`,
        {
          tenantId,
          facilityId,
        }
      );

      return undefined;
    }

    if (!facilityId) {
      setShifts([]);
      setLoading(false);

      setError(
        new Error(
          "Facility shift listener requires facilityId."
        )
      );

      console.warn(
        `${debugLabel} NOT STARTED — facilityId missing`,
        {
          tenantId,
          facilityId,
        }
      );

      return undefined;
    }

    if (
      Array.isArray(allowedFacilityIds) &&
      allowedFacilityIds.length > 0 &&
      !allowedFacilityIds.includes(facilityId)
    ) {
      setShifts([]);
      setLoading(false);

      setError(
        new Error(
          "Facility is outside the authenticated operational scope."
        )
      );

      console.error(
        `${debugLabel} BLOCKED — facility outside allowed scope`,
        {
          tenantId,
          facilityId,
          allowedFacilityIds,
        }
      );

      return undefined;
    }

    setLoading(true);
    setError(null);

    console.log(`${debugLabel} START`, {
      tenantId,
      facilityId,
    });

    const shiftsQuery = query(
      collection(db, "shifts"),

      where(
        "tenantId",
        "==",
        tenantId
      ),

      where(
        "facilityId",
        "==",
        facilityId
      ),

      orderBy(
        "startTime",
        "asc"
      )
    );

    const unsubscribe = onSnapshot(
      shiftsQuery,

      (snapshot) => {
        const rows = snapshot.docs
          .map((shiftDoc) => ({
            id: shiftDoc.id,
            ...shiftDoc.data(),
          }))
          .filter(
            (shift) =>
              shift?.archived !== true
          );

        console.log(
          `${debugLabel} SUCCESS`,
          {
            tenantId,
            facilityId,
            size: rows.length,
          }
        );

        setShifts(rows);
        setLoading(false);
        setError(null);
      },

      (listenerError) => {
        console.error(
          `${debugLabel} ERROR`,
          {
            tenantId,
            facilityId,
            code:
              listenerError?.code,
            message:
              listenerError?.message,
            error:
              listenerError,
          }
        );

        setShifts([]);
        setLoading(false);
        setError(listenerError);
      }
    );

    return () => {
      console.log(`${debugLabel} STOP`, {
        tenantId,
        facilityId,
      });

      unsubscribe();
    };
  }, [
    tenantId,
    facilityId,
    allowedFacilityKey,
    enabled,
    debugLabel,
  ]);

  return {
    shifts,
    loading,
    error,
  };
}